import React from "react";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";
import { performAjaxRequest } from '../utils/ajax_utils';
import { Serializer, SvgRegistry, ComputedUpdater, setLicenseKey } from "survey-core";
import { settings, editorLocalization } from "survey-creator-core";
import "survey-core/survey.i18n";
import "survey-creator-core/survey-creator-core.i18n";
// import "survey-core/defaultV2.css";
import "survey-core/survey-core.css";
import "survey-creator-core/survey-creator-core.css";
import ReactDOMServer from "react-dom/server";
import { LockIcon, SwitchOnIcon, SwitchOffIcon } from './survey/custom_icons';
import {initializeCompanyTheme, setCompanyStyles} from '../styles/company_theme';

const disableClass = 'opacity-25 pe-none';
const RESTRICTED_PROPERTIES = ["choices", "title"];

class SurveyCreatorRenderComponent extends React.Component {
  constructor(props) {
    super(props);

    // Hide the "Add Question" button from the design surface
    settings.designer.showAddQuestionButton = false;

    setLicenseKey(gon.surveyjs_license);

    const lockIconSvg = ReactDOMServer.renderToString(<LockIcon />);
    SvgRegistry.registerIconFromSvg("locked", lockIconSvg);

    // Register custom switch icons for required toggle
    const switchOnSvg = ReactDOMServer.renderToString(<SwitchOnIcon />);
    SvgRegistry.registerIconFromSvg("switchon", switchOnSvg);

    const switchOffSvg = ReactDOMServer.renderToString(<SwitchOffIcon />);
    SvgRegistry.registerIconFromSvg("switchoff", switchOffSvg);

    // Default content for new questions
    const defaultQuestionContent = {
      title: "Badge Question",
      elements: [{ name: "Question 1", type: "text", title: "Question 1" }],
    }

    // Get question data from props
    this.questionId = props.questionId || null;
    this.questionContent = props.questionContent || defaultQuestionContent;
    this.hasResponses = props.hasResponses || false;

    // Initialize company theme
    this.companyTheme = initializeCompanyTheme(gon.company_customizations);

    // Get URL and method from props
    this.url = props.url || '';
    this.method = props.method || '';

    // Determine if we're editing or creating
    this.isEditing = !!this.questionId;

    const options = {
      showLogicTab: false,
      allowZoom: false,
      showTranslationTab: false,
      showThemeTab: false,
      showPreviewTab: false,
      showJSONEditorTab: false,
      pageEditMode: "single",
      questionTypes: ["text", "comment", "checkbox", "radiogroup", "boolean", "dropdown", "tagbox"],
      isAutoSave: this.isEditing,
      propertyGridNavigationMode: "accordion"
    };

    // Initialize the creator
    this.creator = new SurveyCreator(options);
    this.creator.expandCollapseButtonVisibility = "never";

    this.localizeEditor();

    // Set modified so that the save button is enabled
    if (!this.isEditing) {
      this.creator.setModified({})
    }

    Serializer.addProperty("itemvalue", {
      name: "archive",
      type: "boolean",
      category: "general",
    });

    // Configure the survey creator UI
    this.creator.showSidebar = false;
    this.creator.toolboxLocation = "none";

    // Remove unnecessary toolbar actions
    // this.removeToolbarAction("svd-grid-expand");
    this.removeToolbarAction("svd-settings");

    // Hide unnecessary properties
    this.configureProperties();

    this.configureAdvancedUiSettings();

    // Apply user restrictions - moved after all creator initialization is complete
    this.configureRestrictions();

    this.setupBannerDetection();

    const saveAction = this.creator.toolbar.getActionById("svd-save");
    saveAction.showTitle = true;
    saveAction.title = this.isEditing ? "Update Question" : "Create Question";
    saveAction.css = "btn-save-survey";
    saveAction.iconName = "";
    saveAction.tooltip = this.isEditing ? "Update Question" : "Create Question";

    // Set initial JSON content
    this.creator.JSON = this.questionContent;

    // Set save handler
    this.creator.saveSurveyFunc = this.saveSurveyFunc.bind(this);
  }

  setupBannerDetection() {
    this.creator.onSurveyInstanceCreated.add((sender, options) => {
      if (options.area === 'designer-tab') {
        options.survey.onAfterRenderQuestion.add((sender, options) => {
          this.addBannerIfNeeded(options.question, options.htmlElement);
        });
      }
    });
  }

  getBannerMessage(question) {
    const questionType = question.getType();
    const unsupportedMessage = "This question type isn't supported on mobile and won't appear there.";

    if (questionType === 'tagbox') {
        return unsupportedMessage;
    }

    if (questionType === 'text') {
      const inputType = question.inputType;
      if (inputType === 'date' || inputType === 'datetime-local') {
          return unsupportedMessage;
      }
    }

    return null;
  }

  addBannerIfNeeded(question, htmlElement) {
    if (!question || !htmlElement) return;

    const message = this.getBannerMessage(question);

    // Remove any existing banner
    const existingBanner = htmlElement.querySelector('.question-banner');
    if (existingBanner) {
      existingBanner.remove();
    }

    if (message) {
      const bannerDiv = document.createElement('div');
      bannerDiv.className = 'question-banner alert alert-warning text-center mb-2 overflow-auto';
      bannerDiv.setAttribute('role', 'alert');
      bannerDiv.textContent = message;

      const questionHeader = htmlElement.querySelector('.sd-question__header');
      if (questionHeader) {
        questionHeader.parentNode.insertBefore(bannerDiv, questionHeader);
      } else {
        htmlElement.insertBefore(bannerDiv, htmlElement.firstChild);
      }
    }
  }

  localizeEditor() {
    var lang = editorLocalization.getLocale("en");
    lang.ed.survey = "Questionnaire";
    lang.ed.settings = "Questionnaire Settings";
    lang.ed.surveyTypeName = "Questionnaire";
  }

  // Configure restrictions for users
  configureRestrictions() {
    // Add a safety check to ensure these properties exist
    if (!this.creator) return;

    if (this.creator.onElementAllowOperations) {
      this.creator.onElementAllowOperations.add((_, options) => {
          options.allowCopy = false;
          const element = options.element;
          if (element.isQuestion) {
              options.allowDelete = false;
          }
          if (element.isPage || element.isPanel) {
              options.allowDelete = element.questions.length === 0;
          }
      });
    }

    this.creator.onDragDropAllow.add((_, options) => {
      // Prevent dragging and dropping elements
      options.allow = false;
    });
  }

  removeToolbarAction(actionId) {
    const actionIndex = this.creator.toolbar.actions.indexOf(
      this.creator.toolbar.actions.find((action) => action.id === actionId)
    );
    if (actionIndex !== -1) {
      this.creator.toolbar.actions.splice(actionIndex, 1);
    }
  }

  configureProperties() {
    // Hide unnecessary survey properties
    Serializer.findProperty("survey", "logo").visible = false;
    Serializer.findProperty("survey", "description").visible = false;
    Serializer.findProperty("survey", "title").visible = false;

    // Hide undo/redo buttons
    const undoAction = this.creator.toolbar.getActionById("action-undo");
    const redoAction = this.creator.toolbar.getActionById("action-redo");
    if (undoAction) undoAction.css = "d-none";
    if (redoAction) redoAction.css = "d-none";
  }

  configureAdvancedUiSettings() {
    this.creator.onSurveyInstanceCreated.add((sender, options) => {
      if(options.area === "property-grid") {
        const survey = options.survey;
        const panelsToRemove = ["navigation", "logic", "timer", "data", "logo", "logoWidth", "logoHeight", "logoFit", "logoPosition", "layout", "validation", "choicesByUrl"];
        panelsToRemove.forEach(tabName => {
          const panel = survey.getPanelByName(tabName);
          if(panel) {
            panel.delete();
          }
        });

        // Add adorner to the choices to enable/disable it
        // since archive property cannot be edited in read only mode
        options.survey.onGetMatrixRowActions.add((sender, options) => {
          if (options.question.name === "choices") {
            const row = options.row;
            const cQ = row.getQuestionByColumnName("archive");
            const titleUpdater = new ComputedUpdater(() => {
              return cQ.value === true ? "Unarchive" : "Archive";
            });
            if (cQ) {
              options.actions.push({
                location: "end",
                title: titleUpdater,
                action: () => {
                  cQ.value = !cQ.value;
                },
              });
            }
          }
        });
      }

      if (options.area === 'designer-tab' ) {
        options.survey.onUpdateChoiceItemCss.add((sender, options) => {
          if(options.item.archive) {
            options.css += " " + disableClass;
          }
          const choreItems = ['', 'none', 'other'];
          if (this.hasResponses && (choreItems.includes(options.item.value) || options.item.isGhost)) {
            options.css += " " + disableClass;
          }
        });
      }

      options.survey.onGetQuestionTitleActions.add((_, options) => {
        let titleActions = [];

        // Always show the required toggle button, but disable it when the question is locked
        const iconNameUpdater = new ComputedUpdater(() => {
          return options.question.isRequired ? "icon-switchon" : "icon-switchoff";
        });

        const tooltipUpdater = new ComputedUpdater(() => {
          return options.question.isRequired ? "Required" : "Not Required";
        });

        const cssUpdater = new ComputedUpdater(() => {
          let baseCss = "sv-action-bar-item";
          if (this.hasResponses) {
            baseCss += " " + disableClass;
          }
          return baseCss;
        });

        titleActions.push({
          id: "custom-required-toggle",
          iconName: iconNameUpdater,
          disableShrink: true,
          title: 'Required',
          tooltip: tooltipUpdater,
          css: cssUpdater,
          innerCss: "sv-action-bar-item",
          action: () => {
            // Only allow toggling if the question is not locked
            if (!this.hasResponses) {
              options.question.isRequired = !options.question.isRequired;
            }
          },
        });

        // Always show the locked icon when hasResponses is true
        if (this.hasResponses) {
          const tooltipText = "This question cannot be edited because it has already collected responses.";
          titleActions.push({
            id: "show-description-locked",
            iconName: "icon-locked",
            disableShrink: true,
            tooltip: tooltipText,
            innerCss: "locked-icon",
            action: () => {}, // No action defined
          });
        }

        options.titleActions = titleActions;
      });
    });

    this.creator.onElementAllowOperations.add((sender, options) => {
      // Hides the default required switch button by surveyjs
      // https://surveyjs.io/survey-creator/documentation/customize-survey-creation-process
      options.allowChangeRequired = false;
      const locked_question = this.hasResponses
      if ( locked_question) {
        options.allowChangeType = false;
        options.allowEdit = false;
        options.allowDelete = false;
      }
    });

    this.creator.onGetPropertyReadOnly.add((sender, options) => {
      if (this.hasResponses && RESTRICTED_PROPERTIES.includes(options.property.name)) {
        options.readOnly = true;
      }
    });

    this.creator.onCollectionItemAllowOperations.add((sender, options) => {
      if (this.hasResponses) {
        options.allowDelete = false;
        options.allowEdit = false;
      }
    })

    // Hide unnecessry properties in the property grid
    this.creator.onShowingProperty.add((sender, options) => {
      const allowList = [
        "title", "description", "choices", "archive",
        "value", "text", "icon"
      ]
      if (!allowList.includes(options.property.name)) {
        options.canShow = false;
      }
    });
  }

  saveSurveyFunc(saveNo, callback) {
    performAjaxRequest(
      this.url,
      this.method,
      { badge_question: { content: this.creator.JSON } },
      saveNo,
      (saveNo, isSuccess, response) => {
        callback(saveNo, isSuccess);
        this.handleError(isSuccess, response);
        if (isSuccess) {
          if (!this.isEditing && typeof bootstrap !== 'undefined') {
            const modal = bootstrap.Modal.getInstance(document.getElementById('surveyBuilderModal'));
            if (modal) modal.hide();
          }
        }
      }
    );
  }

  handleError = (isSuccess, response) => {
    const errorSpan = document.getElementById('survey-title-error');
    if (!isSuccess && response?.errors) {
      errorSpan.classList.remove('d-none');
      errorSpan.textContent = response.errors[0];
    } else {
      errorSpan.textContent = '';
      errorSpan.classList.add('d-none');
    }
  };

  // Set the company styles for the designer tab
  componentDidMount() {
    const root = document.documentElement;
    setCompanyStyles(root, this.companyTheme);
  }

  render() {
    return (
      <React.StrictMode>
        <SurveyCreatorComponent creator={this.creator} />
      </React.StrictMode>
    )
  }
}

export default SurveyCreatorRenderComponent;